import {useMemo} from 'react'
import {UnderlineNav} from '@primer/react'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {getOcticon} from '../utils/octicons'
import type {LocalNavigationTab} from '../types'

export interface LocalNavigationProps {
  tabs?: LocalNavigationTab[]
  selectedLink?: string
}

function getTabCount(count: number | undefined) {
  if (count !== undefined && count > 0) {
    if (count >= 5000) {
      return '5k+'
    }
    if (count >= 1000) {
      return `${parseFloat((count / 1000).toFixed(1))}k`
    }
    return count.toString()
  }
}

/** Renders the local tabs and other nav items specific to this particular route */
export function LocalNavigation({tabs, selectedLink}: LocalNavigationProps) {
  const activeTab = useMemo(() => {
    if (!tabs || tabs.length === 0) {
      return undefined
    }

    const currentPath = ssrSafeLocation.pathname + ssrSafeLocation.search

    // First, try to find a tab matching the selectedLink
    if (selectedLink) {
      const selectedLinkTab = tabs.find(tab => tab.selectedLinks?.includes(selectedLink))
      if (selectedLinkTab) {
        return selectedLinkTab
      }
    }

    // If there's no selectedLink match, try to infer the active tab based on the current path
    const urlMatchedTabs = tabs.filter(
      tab => tab.href && (currentPath === tab.href || currentPath.startsWith(`${tab.href}/`)),
    )

    // Return the tab with the longest matching href (most specific match)
    return urlMatchedTabs.sort((a, b) => (b.href?.length || 0) - (a.href?.length || 0))[0]
  }, [tabs, selectedLink])

  if (!tabs || tabs.length === 0) {
    return null
  }

  return (
    <UnderlineNav aria-label="Repository">
      {tabs.map(tab => {
        const Icon = getOcticon(tab.icon)
        const href = tab.href || '#'
        const isCurrentPage = activeTab?.id === tab.id

        return (
          <UnderlineNav.Item
            key={tab.id}
            href={href}
            icon={Icon}
            aria-current={isCurrentPage ? 'page' : undefined}
            counter={getTabCount(tab.count)}
          >
            {tab.label}
          </UnderlineNav.Item>
        )
      })}
    </UnderlineNav>
  )
}

try{ LocalNavigation.displayName ||= 'LocalNavigation' } catch {}
try{ Icon.displayName ||= 'Icon' } catch {}